/**
 * mediaEmbed.ts - classify a music/video link so it can play right in the
 * app instead of bouncing the user out to Drive or YouTube.
 *
 * This is intentionally a pure, defensive classifier: it never trusts the
 * shape of a URL, never builds an iframe src by concatenating an arbitrary
 * string, and only ever returns an embeddable result once the host matches
 * one of a short exact allow-list. Anything else falls back to `none`, and
 * every caller keeps the existing "open in a new tab" link working - an
 * embed that fails to load, or a file whose sharing settings block preview,
 * still leaves the user with a working escape hatch.
 */

export type MediaEmbedProvider = 'youtube' | 'drive' | 'vimeo';

export type MediaEmbed =
  | { kind: 'audio'; playUrl: string }
  | { kind: 'video'; playUrl: string }
  | { kind: 'iframe'; embedUrl: string; provider: MediaEmbedProvider }
  | { kind: 'none' };

const AUDIO_EXTENSIONS = new Set(['mp3', 'm4a', 'wav', 'aac', 'ogg']);
// The containers a browser's own <video> element can actually decode, and the
// ones the rest of this app already treats as video: r2Playback.mjs signs a
// Save for exactly these five, videoDownload.ts keeps their extensions, and
// PublicClass.tsx tests for four of them.
//
// m4v and 3gp joined this list on 2026-08-28. Both were accepted for upload
// and saveable, but not recognised here - so they rendered NO player at all
// and left a family with nothing but an "Open in a new tab" link, which is
// what a browser downloads. That stayed invisible while videos were GCS-homed,
// because playback preferred the transcoder's output and that is always named
// 1080p.mp4; the moment the R2 migration started serving originals under their
// own extensions it became Bobbie's *"videos stopped playing in app like they
// are supposed to and now just downloads."*
//
// NOT independently verified by an actual playback test: this session tried,
// with the sandboxed headless Chromium available here, and its
// canPlayType() answered "" (cannot play) for BOTH 3gpp and an mp4 with an
// explicit H.264 codec string - which almost certainly means that build has
// no proprietary codec support at all, not that real Chrome/Edge/Safari
// reject these containers, since mp4/H.264 undeniably plays across this
// app already. That makes the check inconclusive rather than negative, so
// m4v and 3gp stay recognised here on the strength of what a real browser's
// video element reports it supports. If either one ever fails for a real
// family, the "Open in a new tab" / Save video escape hatch every caller
// keeps alongside this player still hands over the same file, working.
//
// avi (video/x-msvideo) is DELIBERATELY ABSENT and is not an oversight. No
// browser ships an AVI decoder for <video>, so recognising it would replace a
// link that works with a player that never can. It is still accepted for
// upload, where it remains a perfectly good file to hand someone.
const VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'm4v', 'webm', '3gp']);

// The upload allowlist still accepts these (attachmentUpload.mjs's
// ALLOWED_VIDEO_TYPES), and each one lands here with no player and nothing
// but a bare "open in a new tab" link, which a browser just downloads. That
// used to read as a broken player rather than an expected limit. avi is the
// only one live today; the set exists so a caller can name the reason
// instead of guessing at a silent 'none'.
const KNOWN_UNPLAYABLE_VIDEO_EXTENSIONS = new Set(['avi']);

/**
 * A short reason a recognized-but-unplayable video file has no player, or
 * null when the url is not one of those (either it plays fine, or it is not
 * a video file this app knows about at all - a caller should stay silent
 * either way rather than guess).
 */
export function unplayableVideoReason(rawUrl: string): string | null {
  const url = safeParseUrl(rawUrl);
  if (!url) return null;
  const ext = extensionOf(url.pathname);
  if (!KNOWN_UNPLAYABLE_VIDEO_EXTENSIONS.has(ext)) return null;
  return `This is a .${ext} file. No phone or computer browser can play that format in the app, so use the link below to open or save it instead.`;
}

// Exact hostname allow-lists. A host is only ever matched by exact string
// equality against one of these - never `.includes()` or a regex that could
// be tricked by a hostile subdomain like "youtube.com.evil.example".
const YOUTUBE_WATCH_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
]);
const YOUTUBE_SHORT_HOSTS = new Set(['youtu.be', 'www.youtu.be']);
const DRIVE_HOSTS = new Set(['drive.google.com']);
const VIMEO_HOSTS = new Set(['vimeo.com', 'www.vimeo.com']);
const VIMEO_PLAYER_HOSTS = new Set(['player.vimeo.com']);

// YouTube video ids are 11 chars in practice, but a little slack keeps this
// from breaking on a future id-format change without ever accepting
// something that could carry query-string-style payload characters.
const YOUTUBE_ID_RE = /^[a-zA-Z0-9_-]{6,20}$/;
const DRIVE_FILE_ID_RE = /^[a-zA-Z0-9_-]{10,100}$/;
const VIMEO_ID_RE = /^[0-9]{6,12}$/;

/** Parse a URL defensively. Only http/https survive; anything else is `null`. */
function safeParseUrl(raw: string): URL | null {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  try {
    const url = new URL(raw.trim());
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return url;
  } catch {
    return null;
  }
}

/**
 * Lowercase file extension from a URL's pathname, decoded first so a
 * Firebase Storage download URL like
 * ".../o/class-media%2FofferingId%2Fuid%2Fclip.mp3?alt=media&token=..."
 * is read as "clip.mp3", not the raw percent-encoded path. The query string
 * (alt=media&token=...) never enters this check - only the pathname does.
 */
function extensionOf(pathname: string): string {
  let decoded = pathname;
  try {
    decoded = decodeURIComponent(pathname);
  } catch {
    // Malformed percent-encoding - fall back to the raw pathname rather
    // than throwing, and let the extension check below simply not match.
  }
  const lastSegment = decoded.split('/').pop() ?? '';
  const dotIndex = lastSegment.lastIndexOf('.');
  if (dotIndex === -1 || dotIndex === lastSegment.length - 1) return '';
  return lastSegment.slice(dotIndex + 1).toLowerCase();
}

function classifyYouTube(url: URL): MediaEmbed {
  const host = url.hostname.toLowerCase();
  let id = '';

  if (YOUTUBE_WATCH_HOSTS.has(host)) {
    if (url.pathname === '/watch') {
      id = url.searchParams.get('v') ?? '';
    } else {
      const parts = url.pathname.split('/').filter(Boolean);
      // /shorts/{id}, /embed/{id}, /v/{id}
      if (parts.length >= 2 && (parts[0] === 'shorts' || parts[0] === 'embed' || parts[0] === 'v')) {
        id = parts[1] ?? '';
      }
    }
  } else if (YOUTUBE_SHORT_HOSTS.has(host)) {
    const parts = url.pathname.split('/').filter(Boolean);
    id = parts[0] ?? '';
  }

  if (id && YOUTUBE_ID_RE.test(id)) {
    return { kind: 'iframe', embedUrl: `https://www.youtube.com/embed/${id}`, provider: 'youtube' };
  }
  return { kind: 'none' };
}

/**
 * Google Drive preview only works when the file's sharing setting allows
 * "anyone with the link" (or the signed-in viewer already has access) - a
 * Drive file shared restrictively will show Drive's own "request access"
 * screen inside the iframe instead of the video. That is exactly why the
 * open-in-new-tab fallback is never removed from any surface that renders
 * this embed: it is the guaranteed-to-work path when preview is blocked.
 */
function classifyDrive(url: URL): MediaEmbed {
  const match = url.pathname.match(/^\/file\/d\/([^/]+)\/?/);
  const id = match?.[1] ?? '';
  if (id && DRIVE_FILE_ID_RE.test(id)) {
    return { kind: 'iframe', embedUrl: `https://drive.google.com/file/d/${id}/preview`, provider: 'drive' };
  }
  return { kind: 'none' };
}

function classifyVimeo(url: URL): MediaEmbed {
  const host = url.hostname.toLowerCase();
  let id = '';

  if (VIMEO_PLAYER_HOSTS.has(host)) {
    const match = url.pathname.match(/^\/video\/([0-9]+)/);
    id = match?.[1] ?? '';
  } else if (VIMEO_HOSTS.has(host)) {
    const parts = url.pathname.split('/').filter(Boolean);
    id = parts[0] ?? '';
  }

  if (id && VIMEO_ID_RE.test(id)) {
    return { kind: 'iframe', embedUrl: `https://player.vimeo.com/video/${id}`, provider: 'vimeo' };
  }
  return { kind: 'none' };
}

/**
 * Classify a music/video URL into what can play right in the app.
 *
 * Order matters: a direct file extension always wins first (covers both a
 * hand-pasted direct link AND a Firebase Storage download URL from
 * `uploadClassVideo`/task uploads), then a short list of known video-host
 * providers, then `none` for anything unrecognized - which callers should
 * render as a plain "open in a new tab" link exactly like today.
 */
export function classifyMediaUrl(rawUrl: string): MediaEmbed {
  const url = safeParseUrl(rawUrl);
  if (!url) return { kind: 'none' };

  const ext = extensionOf(url.pathname);
  if (AUDIO_EXTENSIONS.has(ext)) {
    return { kind: 'audio', playUrl: url.toString() };
  }
  if (VIDEO_EXTENSIONS.has(ext)) {
    return { kind: 'video', playUrl: url.toString() };
  }

  const host = url.hostname.toLowerCase();
  if (YOUTUBE_WATCH_HOSTS.has(host) || YOUTUBE_SHORT_HOSTS.has(host)) {
    return classifyYouTube(url);
  }
  if (DRIVE_HOSTS.has(host)) {
    return classifyDrive(url);
  }
  if (VIMEO_HOSTS.has(host) || VIMEO_PLAYER_HOSTS.has(host)) {
    return classifyVimeo(url);
  }

  return { kind: 'none' };
}

/** What the CALLER already knows a link is, from its own stored record (a
 * dancer portal item's own `kind`, or which of musicLinks/videoLinks it came
 * from) - independent of whether the URL's extension says so. */
export type MediaKindHint = 'music' | 'video';

/**
 * classifyMediaUrl, plus one extra fallback rung for a URL that parsed as a
 * safe http/https link but matched none of the extension/provider checks -
 * a signed GCS/R2 playback URL is the exact shape that trips this: its path
 * carries no file extension at all (`/o/class-media%2F...?alt=media&token=`
 * for GCS, an opaque presigned path for R2), so `classifyMediaUrl` alone
 * returns `none` and a caller with MediaEmbedPlayer renders NOTHING for a
 * perfectly playable video - Codex, PR #809 review, on
 * DancerMediaRow.tsx: *"it is entirely possible her actual video is
 * extensionless and still downloads after this PR"*.
 *
 * The caller already knows the kind (DancerPortalMedia.kind, or which of
 * musicLinks/videoLinks it was read from) without needing to guess from the
 * URL at all, so when the URL-only classification comes up empty, this
 * trusts that hint instead of leaving the row with no player.
 *
 * NEVER applied to a URL that failed to parse as http/https (javascript:,
 * data:, malformed) - those stay `none` regardless of hint, because a hint
 * is a fact about content, not a licence to embed an unvalidated string.
 * Also never applied to a URL on a KNOWN embed-provider host (YouTube,
 * Drive, Vimeo) that failed that provider's own id parse: that is a broken
 * link for that provider, not an unlabeled direct file, and forcing it into
 * a raw `<video src>`/`<audio src>` would not play either - the existing
 * "open in a new tab" escape hatch stays the honest answer there.
 *
 * ONE more rung, ahead of all of the above: a `blob:` url with a hint. This
 * is the browser's own local-preview URL (`URL.createObjectURL`) for a file
 * the signed-in author just picked, before it is ever uploaded anywhere -
 * the class post composer's live "here is what this will look like" preview
 * (StaffComposer.tsx -> PostCard.tsx's `__preview__` post). It is not typed
 * or pasted by anyone and only ever resolves inside the browser tab that
 * minted it (it 404s everywhere else, including a reload of the same tab),
 * so it carries none of the risk the http(s)-only rule above exists to
 * block - unlike javascript:/data:, it cannot smuggle content, only point at
 * a File object already open in this tab's own memory. Packet J20 (video
 * plays in the app everywhere): without this, the composer preview had to
 * mount its own second `<video>` element, exactly the second player this
 * component exists to make unnecessary. Checked before the http(s)-only
 * parse so a blob: url never has to pretend to be one.
 */
export function classifyMediaUrlWithHint(rawUrl: string, hint: MediaKindHint | null | undefined): MediaEmbed {
  if (typeof rawUrl === 'string' && hint && rawUrl.startsWith('blob:')) {
    return hint === 'music' ? { kind: 'audio', playUrl: rawUrl } : { kind: 'video', playUrl: rawUrl };
  }
  const base = classifyMediaUrl(rawUrl);
  if (base.kind !== 'none' || !hint) return base;

  const url = safeParseUrl(rawUrl);
  if (!url) return base;

  const host = url.hostname.toLowerCase();
  const isKnownProviderHost = YOUTUBE_WATCH_HOSTS.has(host)
    || YOUTUBE_SHORT_HOSTS.has(host)
    || DRIVE_HOSTS.has(host)
    || VIMEO_HOSTS.has(host)
    || VIMEO_PLAYER_HOSTS.has(host);
  if (isKnownProviderHost) return base;

  return hint === 'music'
    ? { kind: 'audio', playUrl: url.toString() }
    : { kind: 'video', playUrl: url.toString() };
}

export interface MediaEscapeHatchInput {
  /** Null while the resolved playback url has not landed yet. */
  url: string | null;
  /** Whether this item is STRUCTURALLY saveable at all (canSaveVideo) - a provider link (YouTube/Drive/Vimeo) never is; a Hub Storage or R2 file always is, independent of whether a save attempt has actually succeeded. */
  saveable: boolean;
  /** classifyMediaUrlWithHint's own kind for the resolved url - null before the url resolves. */
  embedKind: MediaEmbed['kind'] | null;
  /** Whether a real play attempt on a hinted native element already failed (MediaEmbedPlayer's onCannotPlay, the delayed case). */
  runtimeBroken: boolean;
  /**
   * Whether a save attempt on a STRUCTURALLY saveable item genuinely
   * failed at runtime (SaveVideoButton's onCannotSave: a network error, a
   * failed presign, or the file being over VIDEO_FETCH_MAX_BYTES). Only
   * meaningful when `saveable` is true - a structurally unsaveable link
   * was never going to succeed, and that is `saveable`'s own job to say,
   * not a "failure".
   */
  runtimeSaveFailed: boolean;
}

/**
 * components/dancer/DancerMediaRow.tsx's own last-resort "Open the original
 * link" decision, factored out here as a pure function so it can be
 * unit-tested directly against every embed kind rather than only through a
 * rendered DOM - see that file's own header comment for the full reasoning
 * behind each branch.
 *
 * TWO INDEPENDENT TRIGGERS, checked in this order:
 *
 *   1. A save that has ACTUALLY failed on a structurally saveable item
 *      (PR #809 review round 7, Codex). This fires regardless of whether
 *      the item still plays fine in-app: Play and Save answer different
 *      needs (watch it now, versus keep a copy), and if the in-app fetch-
 *      based save breaks - most concretely, a file over
 *      VIDEO_FETCH_MAX_BYTES - the hatch is the only real route to a
 *      WORKING save left: the browser's own native long-press/share-sheet
 *      save over the direct link, which streams straight to disk and
 *      never hits this app's in-memory fetch budget at all. Before this
 *      the SAME condition (structurally saveable, nothing has failed yet)
 *      correctly hid the hatch - Download already looked like the honest
 *      route, until it demonstrably was not one.
 *
 *   2. The item cannot be CONFIRMED playable, and is not saveable at all:
 *      - `embedKind === 'none'`: known immediately, no play attempt needed.
 *      - `runtimeBroken`: a hinted native (`video`/`audio`) element
 *        mounted and genuinely failed - MediaEmbedPlayer's own onError.
 *      - `embedKind === 'iframe'`: a provider embed (YouTube, Drive,
 *        Vimeo). PR #809 review round 5 (Codex): this can NEVER set
 *        runtimeBroken - the iframe branch has no failure signal at all,
 *        because a cross-origin embed-disabled or private page fails
 *        silently inside its OWN origin, invisible to this one. This is a
 *        CLASSIFICATION rule, not a detection one (no iframe-load hack):
 *        an iframe kind is never provably playable, so the hatch shows
 *        alongside it unconditionally.
 *      - Never for `embedKind === 'video'`/`'audio'` UNLESS runtimeBroken:
 *        those have a real onError, so the hatch stays off until one fires.
 *      A structurally saveable item that has NOT (yet) failed a save
 *      short-circuits out of this branch entirely - Download is still the
 *      honest route, so the hatch would be redundant here, not merely
 *      narrow.
 *
 * And one thing that overrides both: no url yet (nothing to link to).
 */
export function shouldShowMediaEscapeHatch({
  url,
  saveable,
  embedKind,
  runtimeBroken,
  runtimeSaveFailed,
}: MediaEscapeHatchInput): boolean {
  if (url === null) return false;
  if (saveable) return runtimeSaveFailed;
  return embedKind === 'none' || runtimeBroken || embedKind === 'iframe';
}
